<template>
    <form ref="form">
        <loading-indicator v-if="loading"/>
        <template v-else>
            <div class="row">
                <div class="col-12">
                    <form-row label="Zeitraum">
                        <form-select v-model="type" :options="selectOptions"/>
                    </form-row>
                </div>
                <template v-if="'manual' === type">
                    <div class="col-6">
                        <form-row label="Von" required>
                            <form-input-date v-model="start" type="date" :max="end"/>
                        </form-row>
                    </div>
                    <div class="col-6">
                        <form-row label="Bis" required>
                            <form-input-date v-model="end" type="date" :min="start"/>
                        </form-row>
                    </div>
                </template>
            </div>
        </template>
    </form>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import FormInputDate from '@/components/form/FormInputDate.vue';
    import FormRow from '@/components/form/FormRow.vue';
    import FormSelect from '@/components/form/FormSelect.vue';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import {Popup, popupDefaultCancelButton} from '@/library/popup';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {ref, reactive, computed, watch, inject, toRefs} from 'vue';

    const portuna      = usePortuna();
    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const props = withDefaults(
        defineProps<{
            id: number;
            resource: string;
            fetchUrl?: string;
        }>(),
        {
            fetchUrl: '',
        },
    );

    const {
              id,
              resource,
              fetchUrl,
          } = toRefs(props);

    const type          = ref('all');
    const start         = ref('');
    const end           = ref('');
    const loading       = ref(false);
    const selectOptions = reactive<SelectOption[]>([
        {
            value: 'all',
            label: 'Alle Einträge',
        },
        {
            value: 'future',
            label: 'Alle zukünftigen Einträge',
        },
        {
            value: 'manual',
            label: 'individueller Zeitraum',
        },
    ]);

    const exportLink = computed(() =>
    {
        let link = (fetchUrl.value! || `/${resource.value!}/calendar/export/id/${id.value!}`) + `/type/${type.value}`;
        if ('manual' === type.value)
        {
            link += `/start/${start.value}/end/${end.value}`;
        }
        return link;
    });

    const buttons = computed(() =>
    {
        return [
            popupDefaultCancelButton,
            {
                type: 'primary',
                title: 'Exportieren',
                icon: 'import_export',
                cmd: (popup: Popup) =>
                {
                    if ('manual' === type.value && (!start.value || !end.value))
                    {
                        TopMessageHandler.error('Geben Sie bitte ein Start- und Enddatum an, um Einträge mit einem individuellen Zeitraum zu exportieren.');

                        return;
                    }

                    const a = document.createElement('a');
                    a.setAttribute('href', exportLink.value);
                    a.setAttribute('download', 'download');
                    a.setAttribute('target', '_blank');
                    a.click();

                    TopMessageHandler.success('Die Einträge wurden erfolgreich exportiert.');

                    popup.close();
                },
            },
        ];
    });

    const setButtons = () =>
    {
        popupEmitter.emit('setButtons', buttons.value);
    };

    setButtons();

    watch(start, setButtons);
    watch(end, setButtons);

    defineExpose(Object.assign({}, portuna));
</script>